import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a pagniated results list with full page loader
 */
export default class ResultsList extends PaginatedList {
  /** @type {boolean} */
  #isLoading = false;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // Setup filter listeners and loader styles
    this.#setupFilterListeners();
    this.#addLoaderStyles();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#cleanup();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#showFullPageLoader();
    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) {
      this.#hideFullPageLoader();
      return;
    }

    // OPTIMIZATION: Skip view transitions for layout changes - they're too slow!
    // Layout changes are just CSS attribute updates, no need for complex animations
    this.#setLayout(value);

    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    sessionStorage.setItem(`product-grid-view-${viewport}`, value);

    // Hide loader immediately - layout change is instant
    this.#hideFullPageLoader();

    /* ORIGINAL SLOW CODE WITH VIEW TRANSITIONS:
    // On mobile, skip view transitions for faster response and better iOS compatibility
    if (!mediaQueryLarge.matches) {
      this.#setLayout(value);
      sessionStorage.setItem('product-grid-view-mobile', value);
      // Hide loader immediately after layout change on mobile
      this.#hideFullPageLoader();
      return;
    }

    try {
      await startViewTransition(() => this.#setLayout(value), ['product-grid']);

      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
      // Hide loader immediately after view transition completes
      this.#hideFullPageLoader();
    } catch (error) {
      console.error('Layout animation error:', error);
      // Hide loader even if there's an error
      this.#hideFullPageLoader();
    }
    */
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * Sets up filter event listeners
   */
  #setupFilterListeners() {
    // Listen for filter update events
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    // // Also listen on the closest section
    // const section = this.closest('.shopify-section');
    // if (section) {
    //   section.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    // }
  }

  /**
   * Handles filter update events and shows loader
   * @param {CustomEvent} event
   */
  #handleFilterUpdate = (event) => {
    this.#showFullPageLoader();

    // Set up mutation observer to detect when DOM updates complete
    this.#observeGridChanges();
  };

  /**
   * Observes grid changes to hide loader when DOM updates complete
   */
  #observeGridChanges() {
    const grid = document.querySelector('.product-grid');
    if (!grid) {
      setTimeout(() => this.#hideFullPageLoader(), 3000);
      return;
    }

    // Create observer to watch for DOM changes
    const observer = new MutationObserver((mutations) => {
      let hasContentChanges = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          hasContentChanges = true;
        }
      });

      if (hasContentChanges) {
        observer.disconnect();

        // Small delay to ensure smooth transition
        setTimeout(() => {
          this.#hideFullPageLoader();
        }, 300);
      }
    });

    // Start observing
    observer.observe(grid, {
      childList: true,
      subtree: true
    });

    // Fallback timeout in case observer doesn't fire
    setTimeout(() => {
      observer.disconnect();
      this.#hideFullPageLoader();
    }, 5000);
  }

  /**
   * Shows the full page loader with black background at 20% opacity
   */
  #showFullPageLoader() {
    if (this.#isLoading) return;

    this.#isLoading = true;

    // Create loader overlay
    const loader = document.createElement('div');
    loader.id = 'full-page-loader';
    loader.innerHTML = `
      <div class="loader-backdrop">
        <div class="loader-content">
          <div class="loader-spinner">
            <svg width="50" height="50" viewBox="0 0 50 50">
              <circle cx="25" cy="25" r="20" stroke="#e5e5e5" stroke-width="4" fill="none"/>
              <circle cx="25" cy="25" r="20" stroke="#666" stroke-width="4" fill="none"
                      stroke-dasharray="125.6" stroke-dashoffset="125.6"
                      class="loader-circle">
              </circle>
            </svg>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(loader);

    // Trigger animation
    requestAnimationFrame(() => {
      loader.classList.add('show');
    });
  }

  /**
   * Hides the full page loader
   */
  #hideFullPageLoader() {
    if (!this.#isLoading) return;

    this.#isLoading = false;

    const loader = document.getElementById('full-page-loader');
    if (loader) {
      loader.classList.add('hide');

      // Remove after animation completes
      setTimeout(() => {
        if (loader.parentNode) {
          loader.parentNode.removeChild(loader);
        }
      }, 400);
    }
  }

  /**
   * Adds loader styles to the document
   */
  #addLoaderStyles() {
    if (document.getElementById('full-page-loader-styles')) return;

    const style = document.createElement('style');
    style.id = 'full-page-loader-styles';
    style.textContent = `
      #full-page-loader {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 10000;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
      }

      #full-page-loader.show {
        opacity: 1;
        visibility: visible;
      }

      #full-page-loader.hide {
        opacity: 0;
        visibility: hidden;
      }

      .loader-backdrop {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.2);
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .loader-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

      }

      .loader-spinner {
        animation: spin 1s linear infinite;
      }

      .loader-circle {
        animation: dash 1.5s ease-in-out infinite;
      }

      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      @keyframes dash {
        0% { stroke-dashoffset: 125.6; }
        50% { stroke-dashoffset: 31.4; }
        100% { stroke-dashoffset: 125.6; }
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * Cleanup method
   */
  #cleanup() {
    // Remove event listeners
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    const section = this.closest('.shopify-section');
    if (section) {
      section.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    }

    // Hide loader if it's showing
    this.#hideFullPageLoader();
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
